import React, { useState, useEffect } from 'react';
import { User, Building2, Pencil, Bell } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import FormPageShell from '../components/Common/FormPageShell';
import FormSection from '../components/Common/FormSection';
import Can from '../components/Common/Can';
import PushPreferencesPanel from '../push/PushPreferencesPanel';
import { userFacingError } from '../i18n/userFacingError';
import { P } from '../constants/permissions';
import { useTranslation } from 'react-i18next';

const Profile = () => {
  const { t } = useTranslation('profile');

  const { user, updateProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    cpf: '',
    company_name: '',
    company_cnpj: '',
    company_phone: '',
    company_email: '',
    company_address: '',
    company_city: '',
    company_state: '',
    company_zip: '',
  });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || user.username || '',
        email: user.email || '',
        phone: user.phone || '',
        cpf: user.cpf || '',
        company_name: user.company_name || '',
        company_cnpj: user.company_cnpj || '',
        company_phone: user.company_phone || '',
        company_email: user.company_email || '',
        company_address: user.company_address || '',
        company_city: user.company_city || '',
        company_state: user.company_state || '',
        company_zip: user.company_zip || '',
      });
    }
  }, [user]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateCPF = (cpf) => {
    if (!cpf) return true; // CPF é opcional
    const cleanCPF = cpf.replace(/\D/g, '');
    if (cleanCPF.length !== 11) return false;
    
    // Verificar se todos os dígitos são iguais
    if (/^(\d)\1{10}$/.test(cleanCPF)) return false;
    
    // Algoritmo de validação do CPF
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += parseInt(cleanCPF.charAt(i)) * (10 - i);
    }
    let remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(cleanCPF.charAt(9))) return false;
    
    sum = 0;
    for (let i = 0; i < 10; i++) {
      sum += parseInt(cleanCPF.charAt(i)) * (11 - i);
    }
    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(cleanCPF.charAt(10))) return false;
    
    return true;
  };

  const validateCNPJ = (cnpj) => {
    if (!cnpj) return true; // CNPJ é opcional
    const cleanCNPJ = cnpj.replace(/\D/g, '');
    if (cleanCNPJ.length !== 14) return false;
    
    // Verificar se todos os dígitos são iguais
    if (/^(\d)\1{13}$/.test(cleanCNPJ)) return false;
    
    // Algoritmo de validação do CNPJ
    let sum = 0;
    let weight = 2;
    for (let i = 11; i >= 0; i--) {
      sum += parseInt(cleanCNPJ.charAt(i)) * weight;
      weight = weight === 9 ? 2 : weight + 1;
    }
    let remainder = sum % 11;
    const firstDigit = remainder < 2 ? 0 : 11 - remainder;
    if (firstDigit !== parseInt(cleanCNPJ.charAt(12))) return false;
    
    sum = 0;
    weight = 2;
    for (let i = 12; i >= 0; i--) {
      sum += parseInt(cleanCNPJ.charAt(i)) * weight;
      weight = weight === 9 ? 2 : weight + 1;
    }
    remainder = sum % 11;
    const secondDigit = remainder < 2 ? 0 : 11 - remainder;
    if (secondDigit !== parseInt(cleanCNPJ.charAt(13))) return false;
    
    return true;
  };

  const validatePhone = (phone) => {
    if (!phone) return true; // Telefone é opcional
    const cleanPhone = phone.replace(/\D/g, '');
    return cleanPhone.length === 10 || cleanPhone.length === 11;
  };

  const validateCEP = (cep) => {
    if (!cep) return true; // CEP é opcional
    const cleanCEP = cep.replace(/\D/g, '');
    return cleanCEP.length === 8;
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email é obrigatório';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Email inválido';
    }
    
    if (formData.phone && !validatePhone(formData.phone)) {
      newErrors.phone = 'Telefone inválido';
    }
    
    if (formData.cpf && !validateCPF(formData.cpf)) {
      newErrors.cpf = 'CPF inválido';
    }
    
    if (formData.company_cnpj && !validateCNPJ(formData.company_cnpj)) {
      newErrors.company_cnpj = 'CNPJ inválido';
    }
    
    if (formData.company_phone && !validatePhone(formData.company_phone)) {
      newErrors.company_phone = 'Telefone da empresa inválido';
    }
    
    if (formData.company_email && !validateEmail(formData.company_email)) {
      newErrors.company_email = 'Email da empresa inválido';
    }
    
    if (formData.company_zip && !validateCEP(formData.company_zip)) {
      newErrors.company_zip = 'CEP inválido';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error(t('formFixErrors'));
      return;
    }
    
    setLoading(true);

    try {
      const result = await updateProfile(formData);
      if (result.success) {
        setIsEditing(false);
        setErrors({});
        toast.success(t('updated'));
      }
    } catch (error) {
      toast.error(t('auth:profileUpdateError'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (user) {
      setFormData({
        name: user.name || user.username || '',
        email: user.email || '',
        phone: user.phone || '',
        cpf: user.cpf || '',
        company_name: user.company_name || '',
        company_cnpj: user.company_cnpj || '',
        company_phone: user.company_phone || '',
        company_email: user.company_email || '',
        company_address: user.company_address || '',
        company_city: user.company_city || '',
        company_state: user.company_state || '',
        company_zip: user.company_zip || '',
      });
    }
    setErrors({});
    setIsEditing(false);
  };

  const formatCPF = (cpf) => {
    if (!cpf) return '';
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const formatCNPJ = (cnpj) => {
    if (!cnpj) return '';
    return cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  };

  const formatPhone = (phone) => {
    if (!phone) return '';
    return phone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  };

  const formatCEP = (cep) => {
    if (!cep) return '';
    return cep.replace(/(\d{5})(\d{3})/, '$1-$2');
  };

  if (!user) {
    return (
      <FormPageShell maxWidth="max-w-4xl">
        <div className="flex h-64 flex-col items-center justify-center rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-primary-600" />
          <p className="text-sm text-gray-600">Carregando perfil...</p>
        </div>
      </FormPageShell>
    );
  }

  // Determinar o tipo de conta baseado nos dados disponíveis
  const isIndividual = user.person_type === 'INDIVIDUAL' || user.person_id;
  const isCompany = user.person_type === 'COMPANY' || user.company_id;

  return (
    <FormPageShell maxWidth="max-w-4xl">
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-primary-dark px-6 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <span className="text-2xl font-bold text-white">
                  {(user.name || user.username) ? (user.name || user.username).charAt(0).toUpperCase() : 'U'}
                </span>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">
                  {user.name || user.username || 'Usuário'}
                </h1>
                <p className="text-primary-light">
                  {user.email || 'email@exemplo.com'}
                </p>
              </div>
            </div>
            <div className="flex space-x-2">
              {!isEditing ? (
                <Can permission={P.PROFILE_VIEW}>
                  <button
                    type="button"
                    onClick={() => setIsEditing(true)}
                    className="inline-flex items-center gap-2 rounded-lg bg-white/20 px-4 py-2 text-white transition-colors hover:bg-white/30"
                  >
                    <Pencil className="h-4 w-4" aria-hidden />
                    Editar
                  </button>
                </Can>
              ) : (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="rounded-lg bg-white/20 px-4 py-2 text-white transition-colors hover:bg-white/30"
                  >
                    Cancelar
                  </button>
                  <Can permission={P.PROFILE_VIEW}>
                    <button
                      type="submit"
                      form="profile-edit-form"
                      disabled={loading}
                      className="rounded-lg bg-white px-4 py-2 text-primary-800 transition-colors hover:bg-white/90 disabled:opacity-50"
                    >
                      {loading ? 'Salvando...' : 'Salvar'}
                    </button>
                  </Can>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="p-5 sm:p-6">
          {isEditing ? (
            <form id="profile-edit-form" onSubmit={handleSubmit} className="form-stack">
              {isIndividual && (
                <FormSection
                  id="profile-pessoal-edit"
                  title={t("personalData")}
                  description="Nome, contato e documento vinculados à sua conta."
                  icon={User}
                  variant="primary"
                >
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="form-group">
                      <label htmlFor="profile-name" className="form-label form-label-required">
                        Nome completo
                      </label>
                      <input
                        id="profile-name"
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className={`form-input ${errors.name ? 'border-danger-500' : ''}`}
                      />
                      {errors.name ? <p className="form-error">{errors.name}</p> : null}
                    </div>
                    <div className="form-group">
                      <label htmlFor="profile-email" className="form-label form-label-required">
                        Email
                      </label>
                      <input
                        id="profile-email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className={`form-input ${errors.email ? 'border-danger-500' : ''}`}
                      />
                      {errors.email ? <p className="form-error">{errors.email}</p> : null}
                    </div>
                    <div className="form-group">
                      <label htmlFor="profile-phone" className="form-label">
                        Telefone
                      </label>
                      <input
                        id="profile-phone"
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="(11) 99999-9999"
                        className={`form-input ${errors.phone ? 'border-danger-500' : ''}`}
                      />
                      {errors.phone ? <p className="form-error">{errors.phone}</p> : null}
                    </div>
                    <div className="form-group">
                      <label htmlFor="profile-cpf" className="form-label">
                        CPF
                      </label>
                      <input
                        id="profile-cpf"
                        type="text"
                        name="cpf"
                        value={formData.cpf}
                        onChange={handleInputChange}
                        placeholder="000.000.000-00"
                        className={`form-input ${errors.cpf ? 'border-danger-500' : ''}`}
                      />
                      {errors.cpf ? <p className="form-error">{errors.cpf}</p> : null}
                    </div>
                  </div>
                </FormSection>
              )}

              {isCompany && (
                <FormSection
                  id="profile-empresa-edit"
                  title={t("companyData")}
                  description="Razão social, CNPJ e endereço corporativo."
                  icon={Building2}
                  variant="slate"
                >
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="form-group">
                      <label htmlFor="profile-company-name" className="form-label">
                        Nome da empresa
                      </label>
                      <input
                        id="profile-company-name"
                        type="text"
                        name="company_name"
                        value={formData.company_name}
                        onChange={handleInputChange}
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="profile-cnpj" className="form-label">
                        CNPJ
                      </label>
                      <input
                        id="profile-cnpj"
                        type="text"
                        name="company_cnpj"
                        value={formData.company_cnpj}
                        onChange={handleInputChange}
                        placeholder="00.000.000/0000-00"
                        className={`form-input ${errors.company_cnpj ? 'border-danger-500' : ''}`}
                      />
                      {errors.company_cnpj ? <p className="form-error">{errors.company_cnpj}</p> : null}
                    </div>
                    <div className="form-group">
                      <label htmlFor="profile-company-phone" className="form-label">
                        Telefone da empresa
                      </label>
                      <input
                        id="profile-company-phone"
                        type="tel"
                        name="company_phone"
                        value={formData.company_phone}
                        onChange={handleInputChange}
                        placeholder="(11) 99999-9999"
                        className={`form-input ${errors.company_phone ? 'border-danger-500' : ''}`}
                      />
                      {errors.company_phone ? <p className="form-error">{errors.company_phone}</p> : null}
                    </div>
                    <div className="form-group">
                      <label htmlFor="profile-company-email" className="form-label">
                        Email da empresa
                      </label>
                      <input
                        id="profile-company-email"
                        type="email"
                        name="company_email"
                        value={formData.company_email}
                        onChange={handleInputChange}
                        className={`form-input ${errors.company_email ? 'border-danger-500' : ''}`}
                      />
                      {errors.company_email ? <p className="form-error">{errors.company_email}</p> : null}
                    </div>
                    <div className="form-group md:col-span-2">
                      <label htmlFor="profile-address" className="form-label">
                        Endereço
                      </label>
                      <input
                        id="profile-address"
                        type="text"
                        name="company_address"
                        value={formData.company_address}
                        onChange={handleInputChange}
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="profile-city" className="form-label">
                        Cidade
                      </label>
                      <input
                        id="profile-city"
                        type="text"
                        name="company_city"
                        value={formData.company_city}
                        onChange={handleInputChange}
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="profile-state" className="form-label">
                        Estado
                      </label>
                      <select
                        id="profile-state"
                        name="company_state"
                        value={formData.company_state}
                        onChange={handleInputChange}
                        className="form-select"
                      >
                        <option value="">Selecione o estado</option>
                        <option value="AC">Acre</option>
                        <option value="AL">Alagoas</option>
                        <option value="AP">Amapá</option>
                        <option value="AM">Amazonas</option>
                        <option value="BA">Bahia</option>
                        <option value="CE">Ceará</option>
                        <option value="DF">Distrito Federal</option>
                        <option value="ES">Espírito Santo</option>
                        <option value="GO">Goiás</option>
                        <option value="MA">Maranhão</option>
                        <option value="MT">Mato Grosso</option>
                        <option value="MS">Mato Grosso do Sul</option>
                        <option value="MG">Minas Gerais</option>
                        <option value="PA">Pará</option>
                        <option value="PB">Paraíba</option>
                        <option value="PR">Paraná</option>
                        <option value="PE">Pernambuco</option>
                        <option value="PI">Piauí</option>
                        <option value="RJ">Rio de Janeiro</option>
                        <option value="RN">Rio Grande do Norte</option>
                        <option value="RS">Rio Grande do Sul</option>
                        <option value="RO">Rondônia</option>
                        <option value="RR">Roraima</option>
                        <option value="SC">Santa Catarina</option>
                        <option value="SP">São Paulo</option>
                        <option value="SE">Sergipe</option>
                        <option value="TO">Tocantins</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="profile-zip" className="form-label">
                        CEP
                      </label>
                      <input
                        id="profile-zip"
                        type="text"
                        name="company_zip"
                        value={formData.company_zip}
                        onChange={handleInputChange}
                        placeholder="00000-000"
                        className={`form-input ${errors.company_zip ? 'border-danger-500' : ''}`}
                      />
                      {errors.company_zip ? <p className="form-error">{errors.company_zip}</p> : null}
                    </div>
                  </div>
                </FormSection>
              )}
            </form>
          ) : (
            <div className="form-stack">
              <FormSection
                id="profile-conta"
                title={t("accountInfo")}
                description="Dados retornados pelo servidor ao carregar o perfil (além dos blocos específicos de pessoa ou empresa)."
                icon={User}
                variant="slate"
              >
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="form-group">
                    <span className="form-label">Nome exibido</span>
                    <p className="text-sm text-gray-900">
                      {user.name || user.username || 'Não informado'}
                    </p>
                  </div>
                  <div className="form-group">
                    <span className="form-label">Nome de usuário</span>
                    <p className="text-sm text-gray-900">{user.username || '—'}</p>
                  </div>
                  <div className="form-group">
                    <span className="form-label">E-mail</span>
                    <p className="text-sm text-gray-900">{user.email || 'Não informado'}</p>
                  </div>
                  <div className="form-group">
                    <span className="form-label">ID do usuário</span>
                    <p className="font-mono text-xs text-gray-900">{user.id ?? user.sub ?? '—'}</p>
                  </div>
                  <div className="form-group">
                    <span className="form-label">Tipo de pessoa (cadastro)</span>
                    <p className="text-sm text-gray-900">{user.person_type || '—'}</p>
                  </div>
                  <div className="form-group">
                    <span className="form-label">ID pessoa / empresa</span>
                    <p className="text-sm text-gray-900">
                      {[user.person_id, user.company_id].filter(Boolean).join(' · ') || '—'}
                    </p>
                  </div>
                  <div className="form-group md:col-span-2">
                    <span className="form-label">Papéis</span>
                    <p className="text-sm text-gray-900">
                      {Array.isArray(user.roles) && user.roles.length
                        ? user.roles
                            .map((r) =>
                              typeof r === 'string' ? r : r?.name || r?.code || r?.role || JSON.stringify(r)
                            )
                            .join(', ')
                        : user.role || '—'}
                    </p>
                  </div>
                  <div className="form-group md:col-span-2">
                    <span className="form-label">Conta ativa</span>
                    <p className="text-sm text-gray-900">
                      {user.is_active === false ? 'Não' : user.is_active === true ? 'Sim' : '—'}
                    </p>
                  </div>
                </div>
              </FormSection>

              {isIndividual && (
                <FormSection
                  id="profile-pessoal-view"
                  title={t("personalData")}
                  description="Informações exibidas conforme cadastro."
                  icon={User}
                  variant="primary"
                >
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="form-group">
                      <span className="form-label">Nome completo</span>
                      <p className="text-sm text-gray-900">{user.name || user.username || 'Não informado'}</p>
                    </div>
                    <div className="form-group">
                      <span className="form-label">Email</span>
                      <p className="text-sm text-gray-900">{user.email || 'Não informado'}</p>
                    </div>
                    <div className="form-group">
                      <span className="form-label">Telefone</span>
                      <p className="text-sm text-gray-900">
                        {user.phone ? formatPhone(user.phone) : 'Não informado'}
                      </p>
                    </div>
                    <div className="form-group">
                      <span className="form-label">CPF</span>
                      <p className="text-sm text-gray-900">{user.cpf ? formatCPF(user.cpf) : 'Não informado'}</p>
                    </div>
                  </div>
                </FormSection>
              )}

              {isCompany && (
                <FormSection
                  id="profile-empresa-view"
                  title={t("companyData")}
                  description="Dados corporativos vinculados ao usuário."
                  icon={Building2}
                  variant="slate"
                >
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="form-group">
                      <span className="form-label">Nome da empresa</span>
                      <p className="text-sm text-gray-900">{user.company_name || 'Não informado'}</p>
                    </div>
                    <div className="form-group">
                      <span className="form-label">CNPJ</span>
                      <p className="text-sm text-gray-900">
                        {user.company_cnpj ? formatCNPJ(user.company_cnpj) : 'Não informado'}
                      </p>
                    </div>
                    <div className="form-group">
                      <span className="form-label">Telefone da empresa</span>
                      <p className="text-sm text-gray-900">
                        {user.company_phone ? formatPhone(user.company_phone) : 'Não informado'}
                      </p>
                    </div>
                    <div className="form-group">
                      <span className="form-label">Email da empresa</span>
                      <p className="text-sm text-gray-900">{user.company_email || 'Não informado'}</p>
                    </div>
                    <div className="form-group md:col-span-2">
                      <span className="form-label">Endereço</span>
                      <p className="text-sm text-gray-900">{user.company_address || 'Não informado'}</p>
                    </div>
                    <div className="form-group">
                      <span className="form-label">Cidade</span>
                      <p className="text-sm text-gray-900">{user.company_city || 'Não informado'}</p>
                    </div>
                    <div className="form-group">
                      <span className="form-label">Estado</span>
                      <p className="text-sm text-gray-900">{user.company_state || 'Não informado'}</p>
                    </div>
                    <div className="form-group">
                      <span className="form-label">CEP</span>
                      <p className="text-sm text-gray-900">
                        {user.company_zip ? formatCEP(user.company_zip) : 'Não informado'}
                      </p>
                    </div>
                  </div>
                </FormSection>
              )}
            </div>
          )}

          <div className="form-stack mt-6">
            <FormSection
              id="profile-notificacoes"
              title="Notificações"
              description="Escolha os avisos que você quer receber neste dispositivo."
              icon={Bell}
              variant="primary"
            >
              <PushPreferencesPanel />
            </FormSection>
          </div>
        </div>
      </div>
    </FormPageShell>
  );
};

export default Profile;
